import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { useSaveToast } from '../ui/SaveToast';

// BL-407 round 1b: the company's client service terms, accepted by each client in the client app's first-run setup.
export const TERMS_URL_PATTERN = /^https:\/\/\S+$/;
export const TERMS_VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._ -]{0,39}$/;
export const TERMS_URL_RULE = 'The link must start with https:// and be 500 characters or fewer.';
export const TERMS_VERSION_RULE = 'Use 1 to 40 letters, numbers, spaces, . _ or -, starting with a letter or number.';

export default function ClientTermsSettings({ orgId }) {
    const toast = useSaveToast();
    const [url, setUrl] = useState('');
    const [version, setVersion] = useState('');
    const [savedVersion, setSavedVersion] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    useEffect(() => {
        let active = true;
        if (!orgId) { setBusy(false); return () => { active = false; }; }
        supabaseClient.from('org_settings').select('client_terms_url, client_terms_version').eq('org_id', orgId).maybeSingle()
            .then(({ data, error: failure }) => {
                if (!active) return;
                if (failure) setError(`The service terms could not be loaded: ${failure.message}`);
                else {
                    setUrl(data?.client_terms_url ?? '');
                    setVersion(data?.client_terms_version ?? '');
                    setSavedVersion(data?.client_terms_version ?? '');
                }
            }).catch((failure) => { if (active) setError(`The service terms could not be loaded: ${failure.message}`); })
            .finally(() => { if (active) setBusy(false); });
        return () => { active = false; };
    }, [orgId]);
    async function write() {
        const nextUrl = url.trim();
        const nextVersion = version.trim();
        setBusy(true);
        setError('');
        try {
            const { error: failure } = await supabaseClient.from('org_settings')
                .upsert({ org_id: orgId, client_terms_url: nextUrl, client_terms_version: nextVersion }, { onConflict: 'org_id' });
            if (failure) throw failure;
            setUrl(nextUrl);
            setVersion(nextVersion);
            setSavedVersion(nextVersion);
            toast.saved();
        } catch (failure) {
            setError(`The service terms could not be saved: ${failure.message}`);
            toast.failed(`The service terms could not be saved: ${failure.message}`);
        } finally { setBusy(false); setConfirming(false); }
    }
    function save(event) {
        event.preventDefault();
        if (!TERMS_URL_PATTERN.test(url.trim()) || url.trim().length > 500) { setError(TERMS_URL_RULE); return; }
        if (!TERMS_VERSION_PATTERN.test(version.trim())) { setError(TERMS_VERSION_RULE); return; }
        setError('');
        if (savedVersion && version.trim() !== savedVersion && !confirming) { setConfirming(true); return; }
        write();
    }
    return (
        <section className="rounded-lg border p-4 space-y-3" aria-label="Client service terms">
            <h3 className="font-semibold">Client service terms</h3>
            <form onSubmit={save} className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="client-terms-url">Link to your service terms</label>
                    <input id="client-terms-url" type="url" maxLength={500} required value={url} disabled={busy}
                        onChange={(event) => { setUrl(event.target.value); setConfirming(false); }} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="client-terms-version">Version label</label>
                    <input id="client-terms-version" type="text" maxLength={40} required value={version} disabled={busy}
                        onChange={(event) => { setVersion(event.target.value); setConfirming(false); }} />
                </div>
                <p className="text-sm">Changing the version label means every client has to accept the terms again the next time they open the app.</p>
                {confirming && (
                    <p role="alert" className="text-sm">Every client will be asked to accept the terms again. Click &quot;Yes, change the version&quot; to continue.</p>
                )}
                <button className="px-3 py-2 rounded border" disabled={busy} type="submit">
                    {confirming ? 'Yes, change the version' : 'Save service terms'}
                </button>
                {confirming && (
                    <button className="ml-2 px-3 py-2 rounded border" type="button" onClick={() => setConfirming(false)}>Cancel</button>
                )}
            </form>
            {error && <p role="alert" className="text-red-700">{error}</p>}
        </section>
    );
}
